import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { usePlatform } from '@/hooks/usePlatform';
import { hasFrontlineSession, clearFrontlineSession } from '@/auth/frontlineSession';
import { resetTokenCache } from '@/auth/tokenCache';
import { clearUserCache } from '@/auth/clearUserCache';
import { BASE_PATH, withBase } from '@/utils/basePath';

export type LogoutButtonVariant = 'sidebar' | 'mobile' | 'inline';

interface LogoutButtonProps {
  variant?: LogoutButtonVariant;
}

/**
 * Botón de cierre de sesión reutilizable (Sidebar de escritorio y nav inferior móvil).
 * Siempre aterriza en el login de la app (`/login`), nunca en el home de Pulso.
 *  · Frontline (sin correo corporativo): NO está en Azure AD → se limpia su sesión propia y se va a
 *    `/login`, sin tocar MSAL. Antes caían al logout de Entra (la "página de Microsoft") aunque no
 *    tienen cuenta Microsoft — ese era el bug reportado.
 *  · Cuenta corporativa (Entra): cierre OIDC real (logoutRedirect, limpia el SSO). El
 *    `postLogoutRedirectUri` se deja en `origin` (la ÚNICA Redirect URI registrada; usar `origin/login`
 *    haría que Entra lo rechace y muestre su página genérica — el síntoma que evitamos).
 *  · Sin sesión real (demo/local): navega a `/login`.
 * Dentro de Teams no se renderiza: la sesión la gestiona el SSO de Teams y el redirect de logout está
 * bloqueado dentro del iframe.
 */
export function LogoutButton({ variant = 'sidebar' }: LogoutButtonProps) {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const { platform } = usePlatform();

  if (platform === 'teams') return null;

  function handleLogout() {
    // La cache de token vive en memoria del modulo, no en localStorage, asi que `clearUserCache`
    // no la alcanza. Normalmente da igual: todas las salidas de abajo hacen una navegacion de
    // documento completa, que reinicia el modulo. Pero si `logoutRedirect` RECHAZA, no hay
    // navegacion y la app se queda en pie con un token corporativo valido en cache hasta ~55 min
    // despues de que la persona pulsara salir. Se limpia aqui, antes de cualquier ruta.
    resetTokenCache();

    // QA (2026-08-27) — antes de cualquier ruta de salida: limpia lo que no es parte de la sesión en
    // sí (token/cuenta) pero sí puede hacer que el siguiente usuario, en el mismo navegador, vea
    // información que en realidad es del que se está yendo. Ver `clearUserCache` para el detalle de
    // qué se limpia y qué se conserva a propósito.
    clearUserCache();

    // Frontline: sesión propia (JWT), fuera de Azure AD → limpiar y al login sin pasar por Microsoft.
    if (hasFrontlineSession()) {
      clearFrontlineSession();
      window.location.assign(withBase('/login'));
      return;
    }
    const activeAccount = instance.getActiveAccount?.() ?? accounts[0] ?? null;
    if (activeAccount) {
      instance
        .logoutRedirect({ account: activeAccount, postLogoutRedirectUri: `${window.location.origin}${BASE_PATH}` })
        .catch((error) => {
          if (import.meta.env.DEV) {
            console.error('LogoutButton: falló el cierre de sesión MSAL', error);
          }
        });
    } else {
      window.location.assign(withBase('/login'));
    }
  }

  const label = t('common.logout');

  // 'inline': botón con etiqueta visible para pantallas que NO tienen riel ni nav inferior donde
  // colgarlo — hoy solo `UnregisteredProfileGate` (router/AppRouter.tsx), que se renderiza sin
  // Sidebar ni MobileNav a propósito y por eso se quedaba sin ninguna salida.
  if (variant === 'inline') {
    return (
      <button type="button" className="gate-action-btn" onClick={handleLogout} aria-label={label}>
        <i className="fas fa-sign-out-alt" aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  }

  if (variant === 'mobile') {
    return (
      <button type="button" className="mbn-link mbn-logout" onClick={handleLogout} aria-label={label}>
        <i className="fas fa-sign-out-alt" aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button type="button" className="side-logout-btn" title={label} aria-label={label} onClick={handleLogout}>
      <i className="fas fa-sign-out-alt" aria-hidden="true" />
    </button>
  );
}
